import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import MapView from "../components/MapView";
import Toolbar from "../components/Toolbar";
import StatsBar from "../components/StatsBar";
import PointsPanel from "../components/PointsPanel";
import ElevationProfile from "../components/ElevationProfile";
import ShareDialog from "../components/ShareDialog";
import {
    totalDistance,
    computeElevationStats,
    computeSurfaceStats,
    fetchElevations,
    uid,
} from "../lib/track-utils";
import { toGPX, toKML, toGeoJSON, download, parseAny } from "../lib/gpx";
import {
    loadTracks,
    saveTracks,
    loadCurrent,
    saveCurrent,
} from "../lib/storage";
import { snapToRoads, snapOffroad, analyzeSurface } from "../lib/services";
import { buildShareUrl, readHashTrack } from "../lib/share";
import { useLang } from "../contexts/LanguageContext";
import { t as tGlobal } from "../lib/i18n";

const emptyTrack = () => ({
    id: uid(),
    name: tGlobal("untitledRoute"),
    points: [],
    pois: [],
});

export default function TrackPlanner() {
    const { t } = useLang();
    const [track, setTrack] = useState(() => {
        const shared = readHashTrack();
        if (shared) {
            return {
                id: uid(),
                name: shared.name,
                points: (shared.points || []).map((p) => ({ id: uid(), ...p })),
                pois: (shared.pois || []).map((p) => ({ id: uid(), ...p })),
            };
        }
        return loadCurrent() || emptyTrack();
    });
    const [mode, setMode] = useState("track");
    const [history, setHistory] = useState([]);
    const [savedTracks, setSavedTracks] = useState(() => loadTracks());
    const [showPanel, setShowPanel] = useState(true);
    const [elevationLoading, setElevationLoading] = useState(false);
    const [snapLoading, setSnapLoading] = useState(false);
    const [offroad, setOffroad] = useState(false);
    const [offroadGrade, setOffroadGrade] = useState(1);
    const [flyTarget, setFlyTarget] = useState(null);
    const [showOverlay, setShowOverlay] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareUrl, setShareUrl] = useState("");
    const [showElevation, setShowElevation] = useState(true);
    const [hoverPoint, setHoverPoint] = useState(null);
    const [surfaceLoading, setSurfaceLoading] = useState(false);
    const [activeLayer, setActiveLayer] = useState("osm");
    const [mapInstance, setMapInstance] = useState(null);
    const [toolbarMin, setToolbarMin] = useState(
        () => typeof window !== "undefined" && window.innerWidth < 768,
    );

    useEffect(() => {
        saveCurrent(track);
    }, [track]);

    // Notify on shared load
    useEffect(() => {
        if (readHashTrack()) {
            toast.success(t("toastSharedLoaded"));
            // clean URL hash so further edits don't keep stale state
            window.history.replaceState(
                null,
                "",
                window.location.pathname + window.location.search,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pushHistory = useCallback((t) => {
        setHistory((h) => [...h.slice(-49), t]);
    }, []);

    const addPoint = (latlng) => {
        pushHistory(track);
        setTrack((t) => ({
            ...t,
            points: [...t.points, { id: uid(), ...latlng }],
        }));
    };
    const insertPoint = (idx, latlng) => {
        pushHistory(track);
        setTrack((t) => {
            const next = [...t.points];
            next.splice(idx, 0, { id: uid(), ...latlng });
            return { ...t, points: next };
        });
    };
    const addPoi = (poi) => {
        pushHistory(track);
        setTrack((t) => ({
            ...t,
            pois: [...t.pois, { id: uid(), ...poi }],
        }));
    };
    const movePoint = (id, latlng) => {
        pushHistory(track);
        setTrack((t) => ({
            ...t,
            points: t.points.map((p) =>
                p.id === id ? { ...p, ...latlng, ele: undefined } : p,
            ),
        }));
    };
    const movePoi = (id, latlng) => {
        pushHistory(track);
        setTrack((t) => ({
            ...t,
            pois: t.pois.map((p) => (p.id === id ? { ...p, ...latlng } : p)),
        }));
    };
    const removePoint = (id) => {
        pushHistory(track);
        setTrack((t) => ({ ...t, points: t.points.filter((p) => p.id !== id) }));
    };
    const removePoi = (id) => {
        pushHistory(track);
        setTrack((t) => ({ ...t, pois: t.pois.filter((p) => p.id !== id) }));
    };
    const editPoi = (id, changes) => {
        setTrack((t) => ({
            ...t,
            pois: t.pois.map((p) => (p.id === id ? { ...p, ...changes } : p)),
        }));
    };
    const renameTrack = (name) => setTrack((t) => ({ ...t, name }));
    const undo = () => {
        if (history.length === 0) return;
        const prev = history[history.length - 1];
        setHistory((h) => h.slice(0, -1));
        setTrack(prev);
    };
    const clearAll = () => {
        if (track.points.length === 0 && track.pois.length === 0) return;
        pushHistory(track);
        setTrack((t) => ({ ...t, points: [], pois: [] }));
        toast.success(t("toastCleared"));
    };
    const newTrack = () => {
        pushHistory(track);
        setTrack(emptyTrack());
        toast.success(t("toastNewRoute"));
    };

    const fetchElevation = async () => {
        const pts = track.points;
        if (pts.length === 0) {
            toast.error(t("errAddBeforeElev"));
            return;
        }
        setElevationLoading(true);
        try {
            const elevs = await fetchElevations(pts);
            const allTrackPts = track.points.map((p, i) => ({
                ...p,
                ele: typeof elevs[i] === "number" ? elevs[i] : p.ele,
            }));
            const poiElevs =
                track.pois.length > 0 ? await fetchElevations(track.pois) : [];
            const allPois = track.pois.map((p, i) => ({
                ...p,
                ele: typeof poiElevs[i] === "number" ? poiElevs[i] : p.ele,
            }));
            setTrack((t) => ({ ...t, points: allTrackPts, pois: allPois }));
            const ok = elevs.some((e) => typeof e === "number");
            if (ok) toast.success(t("toastElevOk"));
            else toast.error(t("errElevFail"));
        } catch (e) {
            toast.error(t("errElevError"));
        } finally {
            setElevationLoading(false);
        }
    };

    const doSnap = async (profile) => {
        if (track.points.length < 2) {
            toast.error(t("errNeed2wp"));
            return;
        }
        setSnapLoading(true);
        try {
            const snapped = offroad
                ? await snapOffroad(track.points, profile, offroadGrade)
                : await snapToRoads(track.points, profile);
            if (!snapped) {
                toast.error(t("errNoRoute"));
                return;
            }
            pushHistory(track);
            setTrack((tr) => ({
                ...tr,
                points: snapped.map((p) => ({ id: uid(), ...p })),
            }));
            const modeLabel = offroad
                ? profile === "foot"
                    ? t("modeTrails")
                    : t("modeUnpaved")
                : profile === "driving"
                  ? t("modeRoad")
                  : profile === "cycling"
                    ? t("modeCycle")
                    : t("modeTrails");
            toast.success(
                t("routeTraced", { n: snapped.length, mode: modeLabel }),
            );
        } catch (e) {
            const svc = offroad ? "BRouter" : "OSRM";
            toast.error(t("svcError", { svc, msg: e.message }));
        } finally {
            setSnapLoading(false);
        }
    };

    const analyzeSurfaceNow = async () => {
        if (track.points.length < 2) {
            toast.error(t("errNeed2surface"));
            return;
        }
        setSurfaceLoading(true);
        try {
            const result = await analyzeSurface(track.points);
            if (!result) {
                toast.error(t("errSurfaceFail"));
                return;
            }
            pushHistory(track);
            setTrack((tr) => ({ ...tr, points: result.points }));
            toast.success(
                t("surfaceAnalyzed", {
                    matched: result.matched,
                    total: result.total,
                }),
            );
        } catch (e) {
            toast.error(t("errSurfaceError", { msg: e.message }));
        } finally {
            setSurfaceLoading(false);
        }
    };

    const onImportFile = async (file) => {
        try {
            const text = await file.text();
            const parsed = parseAny(file.name, text);
            const newPoints = (parsed.points || []).map((p) => ({
                id: uid(),
                ...p,
            }));
            const newPois = (parsed.pois || []).map((p) => ({
                id: uid(),
                ...p,
            }));
            pushHistory(track);
            setTrack({
                id: uid(),
                name: parsed.name || file.name.replace(/\.[^.]+$/, ""),
                points: newPoints,
                pois: newPois,
            });
            toast.success(
                t("imported", { p: newPoints.length, poi: newPois.length }),
            );
        } catch (e) {
            toast.error(t("errImport", { msg: e.message }));
        }
    };

    const onExport = (format) => {
        if (track.points.length === 0 && track.pois.length === 0) {
            toast.error(t("errNothingExport"));
            return;
        }
        // Always append the Vulkano_Trackapps suffix to the exported route name.
        const SUFFIX = "Vulkano_Trackapps";
        const baseName = (track.name || "ruta").trim();
        const exportName = baseName.endsWith(SUFFIX)
            ? baseName
            : `${baseName} ${SUFFIX}`;
        const exportTrack = { ...track, name: exportName };
        const safeName =
            exportName.replace(/[^a-z0-9_\-]+/gi, "_") || SUFFIX;
        if (format === "gpx") {
            download(
                `${safeName}.gpx`,
                toGPX(exportTrack),
                "application/gpx+xml",
            );
        } else if (format === "kml") {
            download(
                `${safeName}.kml`,
                toKML(exportTrack),
                "application/vnd.google-earth.kml+xml",
            );
        } else {
            download(
                `${safeName}.geojson`,
                toGeoJSON(exportTrack),
                "application/geo+json",
            );
        }
        toast.success(t("exportedAs", { fmt: format.toUpperCase() }));
    };

    const saveCurrentTrack = () => {
        if (track.points.length === 0 && track.pois.length === 0) {
            toast.error(t("errAddBeforeSave"));
            return;
        }
        const existing = savedTracks.findIndex((t) => t.id === track.id);
        const entry = { ...track, savedAt: new Date().toISOString() };
        const next =
            existing >= 0
                ? savedTracks.map((t, i) => (i === existing ? entry : t))
                : [entry, ...savedTracks];
        setSavedTracks(next);
        saveTracks(next);
        toast.success(t("savedToLibrary"));
    };
    const loadSaved = (id) => {
        const saved = savedTracks.find((x) => x.id === id);
        if (saved) {
            setTrack({
                id: saved.id,
                name: saved.name,
                points: saved.points || [],
                pois: saved.pois || [],
            });
            toast.success(t("loaded", { name: saved.name }));
        }
    };
    const deleteSaved = (id) => {
        const next = savedTracks.filter((x) => x.id !== id);
        setSavedTracks(next);
        saveTracks(next);
        toast.success(t("deletedFromLibrary"));
    };

    const onShare = () => {
        if (track.points.length === 0 && track.pois.length === 0) {
            toast.error(t("errAddBeforeShare"));
            return;
        }
        const url = buildShareUrl(track);
        if (url.length > 8000) {
            toast.error(t("errTooLong"));
            return;
        }
        setShareUrl(url);
        setShareOpen(true);
    };

    const handleSearchSelect = (place) => {
        setFlyTarget({
            lat: place.lat,
            lng: place.lng,
            bbox: place.bbox,
            ts: Date.now(),
        });
        toast.success(t("centeredOn", { name: place.label.split(",")[0] }));
    };

    const stats = useMemo(() => {
        const distance = totalDistance(track.points);
        const ele = computeElevationStats(track.points);
        const surface = computeSurfaceStats(track.points);
        return {
            distance,
            roundtrip: distance * 2,
            altMax: ele.max,
            altMin: ele.min,
            climb: ele.climb,
            descent: ele.descent,
            pointsCount: track.points.length,
            poiCount: track.pois.length,
            unpavedPct: surface.unpavedPct,
            unpavedDist: surface.unpavedDist,
        };
    }, [track]);

    const overlayTracks = useMemo(() => {
        if (!showOverlay) return [];
        return savedTracks.filter((t) => t.id !== track.id);
    }, [showOverlay, savedTracks, track.id]);

    return (
        <div className="relative w-full h-full overflow-hidden">
            <div className="absolute inset-0">
                <MapView
                    points={track.points}
                    pois={track.pois}
                    mode={mode}
                    overlayTracks={overlayTracks}
                    onAddPoint={addPoint}
                    onAddPoi={addPoi}
                    onInsertPoint={insertPoint}
                    onMovePoint={movePoint}
                    onMovePoi={movePoi}
                    onRemovePoint={removePoint}
                    onRemovePoi={removePoi}
                    onEditPoi={editPoi}
                    flyTarget={flyTarget}
                    hoverPoint={hoverPoint}
                    activeLayer={activeLayer}
                    onMapReady={setMapInstance}
                />
            </div>

            <div className="absolute top-4 left-4 right-4 z-[500] pointer-events-none">
                <div className="pointer-events-auto flex flex-wrap items-start gap-2">
                    <Toolbar
                        trackName={track.name}
                        onRenameTrack={renameTrack}
                        mode={mode}
                        onModeChange={setMode}
                        onUndo={undo}
                        canUndo={history.length > 0}
                        onClear={clearAll}
                        onImportFile={onImportFile}
                        onExport={onExport}
                        onFetchElevation={fetchElevation}
                        elevationLoading={elevationLoading}
                        onAnalyzeSurface={analyzeSurfaceNow}
                        surfaceLoading={surfaceLoading}
                        onSaveLocal={saveCurrentTrack}
                        savedTracks={savedTracks}
                        onLoadSaved={loadSaved}
                        onDeleteSaved={deleteSaved}
                        onToggleOverlay={() => setShowOverlay((v) => !v)}
                        isOverlayed={showOverlay}
                        onNewTrack={newTrack}
                        onSnap={doSnap}
                        snapLoading={snapLoading}
                        offroad={offroad}
                        onToggleOffroad={setOffroad}
                        offroadGrade={offroadGrade}
                        onChangeGrade={setOffroadGrade}
                        onShare={onShare}
                        minimized={toolbarMin}
                        onToggleMinimize={() => setToolbarMin((v) => !v)}
                        onSearchSelect={handleSearchSelect}
                        onZoomIn={() => mapInstance?.zoomIn()}
                        onZoomOut={() => mapInstance?.zoomOut()}
                        activeLayer={activeLayer}
                        onChangeLayer={setActiveLayer}
                        showPanel={showPanel}
                        onTogglePanel={() => setShowPanel((v) => !v)}
                    />
                </div>
            </div>

            {showPanel && (
                <div
                    className="absolute top-24 right-4 z-[500] pointer-events-auto"
                    style={{ maxHeight: "calc(100vh - 220px)" }}
                >
                    <PointsPanel
                        points={track.points}
                        pois={track.pois}
                        onRemovePoint={removePoint}
                        onRemovePoi={removePoi}
                        onClose={() => setShowPanel(false)}
                    />
                </div>
            )}

            <div
                className="absolute left-4 z-[500] pointer-events-none"
                style={{
                    bottom: "calc(env(safe-area-inset-bottom, 0px) + 110px)",
                }}
            >
                <div className="chip pointer-events-auto" data-testid="mode-hint">
                    <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{
                            background:
                                mode === "track"
                                    ? "#ff7a18"
                                    : mode === "poi"
                                      ? "#60a5fa"
                                      : "#5a626c",
                        }}
                    ></span>
                    {mode === "track"
                        ? t("hintTrack")
                        : mode === "poi"
                          ? t("hintPoi")
                          : t("hintView")}
                </div>
            </div>

            <div
                className="absolute left-3 right-3 z-[500] pointer-events-none"
                style={{
                    bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
                }}
            >
                <div className="flex flex-col gap-2 pointer-events-auto">
                    {track.points.length > 1 && showElevation && (
                        <ElevationProfile
                            points={track.points}
                            onHover={setHoverPoint}
                            onHide={() => {
                                setShowElevation(false);
                                setHoverPoint(null);
                            }}
                        />
                    )}
                    {track.points.length > 1 && !showElevation && (
                        <button
                            className="rf-btn self-start"
                            onClick={() => setShowElevation(true)}
                            data-testid="show-elevation-btn"
                            title={t("showElevationTitle")}
                        >
                            <i className="fa-solid fa-chart-area"></i> {t("elevationProfile")}
                        </button>
                    )}
                    <StatsBar stats={stats} />
                </div>
            </div>

            <ShareDialog
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                url={shareUrl}
            />
        </div>
    );
}
